export type AdminRouteGroupId =
  | 'people-access'
  | 'catalog-policy'
  | 'field-operations'
  | 'money-controls'
  | 'communications-growth'
  | 'system-data'
  | 'advanced';

export type AdminRouteLifecycle = 'canonical' | 'detail' | 'compatibility' | 'retired';

export type AdminRouteIcon =
  | 'Activity'
  | 'BarChart3'
  | 'BookOpen'
  | 'Building2'
  | 'CalendarCheck'
  | 'ClipboardList'
  | 'Clock'
  | 'DollarSign'
  | 'FileSpreadsheet'
  | 'FileText'
  | 'KanbanSquare'
  | 'KeyRound'
  | 'LayoutGrid'
  | 'Megaphone'
  | 'MessageSquare'
  | 'MoreHorizontal'
  | 'Package'
  | 'Radio'
  | 'Receipt'
  | 'Route'
  | 'Send'
  | 'Settings'
  | 'ShieldCheck'
  | 'Sparkles'
  | 'Target'
  | 'TrendingUp'
  | 'Users'
  | 'Zap';

export type AdminRouteVisibility = Readonly<{
  /** Appears in the grouped Administration shell. */
  navigation: boolean;
  /** Can be found by Cmd/Ctrl+K. */
  commandPalette: boolean;
  /** Appears before a command-palette query is entered. */
  suggested?: boolean;
}>;

export type AdminRouteActiveWhen = Readonly<{
  pathname: string;
  query?: Readonly<Record<string, string>>;
  includeDescendants?: boolean;
}>;

export type AdminRouteDefinition = Readonly<{
  id: string;
  label: string;
  description: string;
  group: AdminRouteGroupId;
  icon: AdminRouteIcon;
  lifecycle: AdminRouteLifecycle;
  /**
   * Next.js route pattern for local routes. Dynamic segments remain patterns;
   * they never receive fabricated IDs merely to make them navigable.
   */
  pathPattern: string;
  /** Concrete destination when this route can be opened without runtime data. */
  href?: string;
  /** Destination used instead of a compatibility route. */
  canonicalHref?: string;
  searchTerms: readonly string[];
  visibility: AdminRouteVisibility;
  /** Local page inventory entries are distinguished from cross-hub destinations. */
  surface: 'route' | 'destination';
  /** Admin pages are guarded by the admin layout; account security is broader. */
  audience: 'admin' | 'authenticated';
  activeWhen?: AdminRouteActiveWhen;
}>;

export type AdminRouteGroupDefinition = Readonly<{
  id: AdminRouteGroupId;
  label: string;
  description: string;
  icon: AdminRouteIcon;
}>;

export type AdminNavigationGroup = AdminRouteGroupDefinition & Readonly<{
  routes: readonly AdminRouteDefinition[];
}>;

export const ADMIN_ROUTE_GROUPS = [
  {
    id: 'people-access',
    label: 'People & access',
    description: 'Team accounts, personal security, permissions, and administrative history.',
    icon: 'Users',
  },
  {
    id: 'catalog-policy',
    label: 'Catalog & policy',
    description: 'What the company sells, where it works, and the rules that shape every quote.',
    icon: 'ClipboardList',
  },
  {
    id: 'field-operations',
    label: 'Field operations',
    description: 'Capacity, service evidence, compliance, subcontractors, and shop execution.',
    icon: 'Route',
  },
  {
    id: 'money-controls',
    label: 'Money controls',
    description: 'Billing policy, economics, rate calibration, and financial evidence.',
    icon: 'Receipt',
  },
  {
    id: 'communications-growth',
    label: 'Communications & growth',
    description: 'Delivery health, templates, review automation, and conversion controls.',
    icon: 'MessageSquare',
  },
  {
    id: 'system-data',
    label: 'System & data',
    description: 'Connected services, imports, automation, and operational health.',
    icon: 'Zap',
  },
  {
    id: 'advanced',
    label: 'Advanced',
    description: 'Evidence browsers and specialist tools used for diagnosis and review.',
    icon: 'MoreHorizontal',
  },
] as const satisfies readonly AdminRouteGroupDefinition[];

export const NAV_SEARCH = { navigation: true, commandPalette: true } as const;
export const SEARCH_ONLY = { navigation: false, commandPalette: true } as const;
export const HIDDEN = { navigation: false, commandPalette: false } as const;
